// src/components/sections/Hero.ts - Version refactorisée
import { statistics, services, mainTexts, animationConfig } from './HeroConfig';
import OptimizedPicture from '../common/OptimizedPicture';
import '../../styles/components/hero.css';

export default class Hero {
  private element: HTMLElement;

  constructor() {
    this.element = document.createElement('section');
    this.element.className = 'hero-section relative text-white pt-0 pb-20 w-full overflow-hidden bg-gradient-to-b from-[#0b1220]/70 via-[#0b1220]/80 to-[#0b1220]/90';
    this.element.id = 'hero-section';

    this.render();
    this.setupEventListeners();

    // Initialiser directement les compteurs avec un délai
    setTimeout(() => {
      this.initCounters();
    }, animationConfig.startDelay);
  }

  /**
   * Initialise les compteurs de statistiques
   */
  private initCounters(): void {
    const statItems = this.element.querySelectorAll('.stat-item');
    statItems.forEach(item => {
      const counter = item.querySelector('.counter') as HTMLElement;
      const value = parseInt(item.getAttribute('data-value') || '0', 10);

      // Animation simplifiée mais directe
      let count = 0;
      const increment = Math.max(1, Math.floor(value / animationConfig.counterSteps));
      const interval = setInterval(() => {
        count += increment;
        if (count >= value) {
          count = value;
          clearInterval(interval);
        }
        counter.textContent = count.toString();
      }, animationConfig.counterIncrementTime);
    });
  }

  /**
   * Rendu du composant
   */
  private render(): void {
    this.element.innerHTML = `
      ${this.renderBackground()}
      <div class="container mx-auto relative z-10">
        <div class="flex flex-col lg:flex-row items-start">
          ${this.renderLeftSection()}
          ${this.renderRightSection()}
        </div>
      </div>
    `;
  }

  /**
   * Rendu de l'arrière-plan dynamique
   */
  private renderBackground(): string {
    return `
      <!-- Fond dynamique moderne -->
      <div class="absolute inset-0 overflow-hidden">
        <!-- Grille de fond modernisée avec motif subtil -->
        <div class="absolute inset-0 opacity-20" style="background-image: radial-gradient(rgba(237, 76, 103, 0.1) 1px, transparent 1px), radial-gradient(rgba(52, 152, 219, 0.1) 1px, transparent 1px); background-size: 30px 30px, 25px 25px; background-position: 0 0, 15px 15px;"></div>

        <!-- Dégradé de fond plus intense -->
        <div class="absolute inset-0 bg-gradient-to-b from-[#0b1220] via-[#0b1220]/95 to-[#0b1220]/90"></div>

        <!-- Formes organiques vivantes - plus grandes et plus colorées -->
        <div class="absolute -top-[15%] -right-[5%] w-[70%] h-[70%] bg-gradient-to-br from-[#ed4c67]/25 to-[#3498db]/10 rounded-full blur-3xl opacity-60 wave-anim" style="animation-delay: 0.5s;"></div>
        <div class="absolute -bottom-[20%] -left-[10%] w-[80%] h-[80%] bg-gradient-to-tr from-[#3498db]/25 to-[#ed4c67]/10 rounded-full blur-3xl opacity-60 wave-anim-alt"></div>

        <!-- Cercles radiatifs pulsants -->
        <div class="radiate absolute top-1/4 left-1/4 w-40 h-40 bg-[#ed4c67]/10 rounded-full blur-xl"></div>
        <div class="radiate absolute bottom-1/3 right-1/4 w-60 h-60 bg-[#3498db]/10 rounded-full blur-xl" style="animation-delay: 1s;"></div>
        <div class="radiate absolute top-2/3 left-1/3 w-32 h-32 bg-[#e67e22]/10 rounded-full blur-xl" style="animation-delay: 2s;"></div>

        <!-- Structure de lignes subtiles qui traversent l'écran -->
        <div class="absolute inset-0 opacity-10">
          <svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 100 100" preserveAspectRatio="none">
            <path d="M0,50 C30,30 70,70 100,50 L100,100 L0,100 Z" fill="#3498db" fill-opacity="0.1"></path>
            <path d="M0,60 C40,40 60,80 100,60 L100,100 L0,100 Z" fill="#ed4c67" fill-opacity="0.1"></path>
            <path d="M0,70 C50,50 50,90 100,70 L100,100 L0,100 Z" fill="#e67e22" fill-opacity="0.05"></path>
          </svg>
        </div>
      </div>
    `;
  }

  /**
   * Rendu de la section gauche (texte, CTA, statistiques)
   */
  private renderLeftSection(): string {
    return `
      <!-- SECTION GAUCHE -->
      <div class="w-full lg:w-1/2 mb-16 lg:mb-0 z-10 text-left">
        <!-- Titre avec animation d'apparition séquentielle -->
        <div class="overflow-hidden mb-8">
          <h1 class="text-5xl lg:text-6xl xl:text-7xl font-bold leading-tight">
            <span class="block fade-in-up delay-100 text-white/95">${mainTexts.title.first}</span>
            <span class="block">${mainTexts.title.highlight}</span>
            <span class="block fade-in-up delay-300 mt-2">${mainTexts.title.last}</span>
          </h1>
        </div>

        <!-- Description avec effet d'apparition -->
        <div class="fade-in-up delay-400">
          <p class="text-xl md:text-2xl mb-10 text-white/90 max-w-xl leading-relaxed">
            ${mainTexts.description}
          </p>
        </div>

        <!-- Boutons d'action avec animations avancées -->
        <div class="flex flex-col md:flex-row md:flex-nowrap gap-4 md:gap-6 mb-16 fade-in-up delay-500 w-full md:w-auto">
          ${this.renderPrimaryButton()}
          ${this.renderSecondaryButton()}
        </div>

        <!-- Statistiques avec design moderne et animation -->
        ${this.renderStatistics()}
      </div>
    `;
  }

  /**
   * Rendu du bouton principal
   */
  private renderPrimaryButton(): string {
    const { text, href } = mainTexts.ctaButtons.primary;

    return `
      <!-- Bouton principal avec animation de gradient et effet de survol -->
      <a href="${href}" class="group relative overflow-hidden rounded-full py-4 px-8 flex sm:inline-flex items-center justify-center shine-effect w-full md:w-auto text-center">
        <!-- Fond principal discret -->
        <span class="hero-cta-bg absolute inset-0 rounded-3xl"></span>
        <!-- Halo doux activé au survol -->
        <span class="hero-cta-hover absolute inset-0 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></span>
        <!-- Contenu du bouton -->
        <span class="relative flex items-center justify-center z-10 font-bold text-white transition-transform duration-300 group-hover:scale-105">
          <span class="mr-2">${text}</span>
          <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 transition-transform duration-500 transform group-hover:translate-x-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14 5l7 7m0 0l-7 7m7-7H3" />
          </svg>
        </span>
      </a>
    `;
  }

  /**
   * Rendu du bouton secondaire
   */
  private renderSecondaryButton(): string {
    const { text, href } = mainTexts.ctaButtons.secondary;

    return `
      <!-- Bouton secondaire avec animation de bordure -->
      <a href="${href}" class="group relative overflow-hidden rounded-full py-4 px-8 flex sm:inline-flex items-center justify-center w-full md:w-auto text-center">
        <!-- Fond avec effet glassmorphism plus prononcé -->
        <span class="absolute inset-0 border-2 border-won-blue/30 rounded-full backdrop-blur-sm transition-all duration-300 group-hover:border-won-blue/70 bg-white/5 group-hover:bg-white/10"></span>

        <!-- Contenu du bouton -->
        <span class="relative flex items-center justify-center z-10 font-bold text-white transition-transform duration-300 group-hover:scale-105">
          <span class="mr-2">${text}</span>
          <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 transition-transform duration-500 transform group-hover:translate-x-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
          </svg>
        </span>
      </a>
    `;
  }

  /**
   * Rendu des statistiques
   */
  private renderStatistics(): string {
    return `
      <div class="relative fade-in-up delay-500">
        <!-- Effet de flou en arrière-plan amélioré -->
        <div class="absolute inset-0 bg-white/5 backdrop-blur-xl rounded-2xl -z-10 border border-white/10"></div>

        <div class="grid grid-cols-1 sm:grid-cols-3 gap-6 py-6 px-6 rounded-2xl relative overflow-hidden">
          <!-- Élément décoratif qui tourne lentement -->
          <div class="absolute -bottom-20 -right-20 w-40 h-40 rounded-full bg-gradient-to-br from-[#ed4c67]/20 to-[#3498db]/10 blur-xl" style="animation: rotateSlow 15s linear infinite"></div>

          ${statistics.map(stat => this.renderStat(stat.value, stat.label)).join('')}
        </div>
      </div>
    `;
  }

  /**
   * Rendu d'une statistique individuelle
   */
  private renderStat(value: number, label: string): string {
    return `
      <!-- Statistique -->
      <div class="stat-item relative z-10 text-center group glitch-hover" data-value="${value}">
        <div class="mb-2 relative">
          <div class="absolute -inset-1.5 bg-gradient-to-r from-[#ed4c67]/20 to-transparent rounded-full blur-sm opacity-0 group-hover:opacity-100 transition-opacity"></div>
          <p class="text-3xl lg:text-4xl font-bold relative">+<span class="counter">0</span></p>
        </div>
        <p class="text-white/70 text-lg group-hover:text-white/90 transition-colors">${label}</p>
      </div>
    `;
  }

  /**
   * Rendu de la section droite (visuel principal)
   */
  private renderRightSection(): string {
    return `
      <!-- SECTION DROITE - Visuel principal -->
      <div class="lg:w-1/2 flex justify-center relative z-10">
        <!-- Container principal avec effet 3D modernisé -->
        <div class="card-3d relative w-11/12 lg:w-10/12 fade-in-up delay-300" id="hero-image-container">
          <!-- Halos lumineux dynamiques -->
          <div class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[90%] h-[90%] bg-gradient-to-br from-[#3498db]/20 to-[#ed4c67]/10 rounded-full blur-3xl opacity-70 radiate"></div>
          <div class="absolute top-1/3 left-1/3 -translate-x-1/2 -translate-y-1/2 w-[50%] h-[50%] bg-gradient-to-tl from-[#e67e22]/10 to-[#3498db]/10 rounded-full blur-3xl opacity-50 radiate" style="animation-delay: 1.5s;"></div>

          <!-- Badge tendance animé -->
          <div class="text-right">
            <div class="inline-block fade-in-up mb-4 p-px rounded-full overflow-hidden gradient-primary">
              <div class="bg-[#34495e]/90 backdrop-blur-sm px-4 py-1.5 rounded-full">
                <div class="flex items-center">
                  <span class="flex h-2 w-2 relative mr-2">
                    <span class="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#ed4c67] opacity-75"></span>
                    <span class="relative inline-flex rounded-full h-2 w-2 bg-[#ed4c67]"></span>
                  </span>
                  <span class="text-white/90 text-sm font-medium">${mainTexts.badge}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Carte principale avec effet de glassmorphism avancé -->
          ${this.renderMainCard()}
        </div>
      </div>
    `;
  }

  /**
   * Rendu de la carte principale
   */
  private renderMainCard(): string {
    return `
      <div class="relative rounded-2xl overflow-hidden transform transition-all duration-700 hover:scale-[1.02] hover:rotate-1">
        <!-- Bordure avec dégradé animé -->
        <div class="absolute inset-0 p-px rounded-2xl overflow-hidden">
          <div class="absolute inset-0 bg-gradient-to-r from-[#ed4c67] via-[#3498db] to-[#ed4c67] rounded-2xl opacity-70" style="background-size: 200% 200%; animation: morphGradient 8s linear infinite;"></div>
        </div>

        <!-- Contenu de la carte avec fond glassmorphism -->
        <div class="relative bg-gradient-to-br from-[#34495e]/90 to-[#131313]/90 backdrop-blur-xl border border-white/10 rounded-2xl p-6 overflow-hidden">
          <!-- Éléments de design géométriques -->
          <div class="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-[#ed4c67]/10 to-[#3498db]/5 rounded-full blur-xl -translate-y-1/2 translate-x-1/2"></div>
          <div class="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-tr from-[#3498db]/10 to-[#ed4c67]/5 rounded-full blur-xl translate-y-1/2 -translate-x-1/2"></div>

          <!-- Image principale avec ombre portée -->
          <div class="relative mb-6 rounded-xl overflow-hidden shadow-2xl shine-effect group">
            <div class="absolute inset-0 bg-gradient-to-tr from-[#34495e]/50 to-[#131313]/20 group-hover:opacity-0 transition-opacity duration-500"></div>
            ${OptimizedPicture.create({
              src: 'AccueilWONNew2',
              alt: 'Digital Transformation - World of Numeric',
              className: 'w-full h-auto rounded-xl relative z-10',
              loading: 'eager',
              sizes: '(max-width: 768px) 400px, (max-width: 1200px) 800px, 1200px'
            })}
          </div>

          <!-- Titre de section -->
          <h2 class="text-xl font-semibold text-white/90 mb-4 pb-2 border-b border-white/10 relative">
            <span class="relative z-10">Solutions numériques</span>
            <span class="absolute left-0 bottom-0 w-1/4 h-px bg-gradient-to-r from-[#ed4c67] to-transparent"></span>
          </h2>

          <!-- Services - grille moderne avec icônes -->
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
            ${services.map(service => this.renderServiceCard(service)).join('')}
          </div>

          <!-- Badge et CTA animés -->
          <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mt-6 pt-4 border-t border-white/10">
            <!-- Badge technologie -->
            <div class="bg-gradient-to-r from-[#ed4c67]/10 to-[#3498db]/10 backdrop-blur-sm px-3 py-1.5 rounded-full text-xs text-white/70 border border-white/10 flex items-center space-x-1 hover:text-white/90 transition-colors duration-300">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 text-[#ed4c67]" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
              </svg>
              <span>Technologies de pointe</span>
            </div>

            <!-- Micro CTA -->
            <a href="#services" class="group flex items-center text-sm text-white/70 hover:text-white transition-colors duration-300">
              <span>En savoir plus</span>
              <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 ml-1 transform transition-transform duration-300 group-hover:translate-x-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14 5l7 7m0 0l-7 7m7-7H3" />
              </svg>
            </a>
          </div>
        </div>
      </div>

      <!-- Éléments flottants pour un effet de profondeur -->
      <div class="absolute top-5 left-10 w-8 h-8 bg-[#ed4c67]/30 rounded-full filter blur-sm wave-anim"></div>
      <div class="absolute bottom-10 right-5 w-6 h-6 bg-[#3498db]/30 rounded-full filter blur-sm wave-anim-alt" style="animation-delay: 1s;"></div>
      <div class="absolute top-1/2 right-0 w-4 h-4 bg-[#e67e22]/30 rounded-full filter blur-sm wave-anim" style="animation-delay: 1.5s;"></div>
    `;
  }

  /**
   * Rendu d'une carte de service
   */
  private renderServiceCard(service: any): string {
    return `
      <!-- Service: ${service.title} -->
      <div class="group relative bg-white/5 hover:bg-white/10 rounded-xl p-3 transition-all duration-300 overflow-hidden">
        <div class="absolute inset-0 bg-gradient-to-br from-[${service.color}]/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
        <div class="flex items-center space-x-3 relative z-10">
          <div class="flex-shrink-0 w-10 h-10 rounded-full bg-[${service.color}]/20 flex items-center justify-center group-hover:bg-[${service.color}]/30 transition-colors duration-300">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-[${service.color}]" viewBox="0 0 20 20" fill="currentColor">
              ${service.icon}
            </svg>
          </div>
          <div>
            <p class="font-medium text-white group-hover:text-white transition-colors duration-300">${service.title}</p>
            <p class="text-xs text-white/60 group-hover:text-white/80 transition-colors duration-300">${service.subtitle}</p>
          </div>
        </div>
      </div>
    `;
  }

  /**
   * Configuration des écouteurs d'événements
   */
  private setupEventListeners(): void {
    // Effet de parallaxe au mouvement de la souris
    this.setupParallaxEffect();

    // Animation des compteurs lors du défilement
    this.setupScrollTriggers();
  }

  /**
   * Configuration de l'effet de parallaxe
   */
  private setupParallaxEffect(): void {
    const heroContainer = this.element.querySelector('#hero-image-container') as HTMLElement | null;
    if (!heroContainer) return;

    let targetX = 0;
    let targetY = 0;
    let currentX = 0;
    let currentY = 0;

    // Écouteur pour détecter le mouvement de la souris
    document.addEventListener('mousemove', (e) => {
      const x = e.clientX / window.innerWidth;
      const y = e.clientY / window.innerHeight;

      // Calculer le mouvement avec une amplitude plus faible
      targetX = (x - 0.5) * 20;
      targetY = (y - 0.5) * 20;
    });

    // Fonction pour animer en douceur
    const animate = () => {
      // Interpolation fluide avec inertie réduite
      const dx = targetX - currentX;
      const dy = targetY - currentY;

      currentX += dx * 0.05;
      currentY += dy * 0.05;

      // Appliquer la transformation
      (heroContainer as HTMLElement).style.transform = `translate(${currentX}px, ${currentY}px)`;

      // Continuer l'animation
      requestAnimationFrame(animate);
    };

    // Démarrer l'animation
    animate();
  }

  /**
   * Configuration des déclencheurs de défilement
   */
  private setupScrollTriggers(): void {
    const statItems = this.element.querySelectorAll('.stat-item');

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const counter = entry.target.querySelector('.counter') as HTMLElement;
          const value = parseInt(entry.target.getAttribute('data-value') || '0', 10);

          // Réinitialiser et animer
          if (counter) {
            counter.textContent = '0';
            let count = 0;
            const increment = Math.max(1, Math.floor(value / animationConfig.counterSteps));
            const interval = setInterval(() => {
              count += increment;
              if (count >= value) {
                count = value;
                clearInterval(interval);
              }
              counter.textContent = count.toString();
            }, animationConfig.counterIncrementTime);
          }

          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.5 });

    statItems.forEach(item => observer.observe(item));
  }

  /**
   * Retourne l'élément du composant
   */
  public getElement(): HTMLElement {
    return this.element;
  }
}
